import * as T from "three";
import { lightStylePatterns, lightStyleValue } from "./lightstyles.js";
import { logger } from "./log.js";
const numbers = value => {
    const result = String(value ?? "0 0 0").trim().split(/\s+/).slice(0, 3).map(Number);
    return result.length === 3 && result.every(Number.isFinite) ? result : [0, 0, 0];
};
const log = logger("Lighting");
const num = (value, fallback = 0) => Number.isFinite(Number(value)) ? Number(value) : fallback;
export function lightDirection(entity, entities = [], compiler = false) {
    const target = entity.target && entities.find(e => e.targetname === entity.target);
    if (target) return new T.Vector3(...numbers(target.origin)).sub(new T.Vector3(...numbers(entity.origin))).normalize();
    const angles = numbers(entity.angles), yaw = (num(entity.angle) || angles[1]) * Math.PI / 180,
        pitch = (num(entity.pitch) || (compiler ? angles[0] : -angles[0])) * Math.PI / 180;
    if (num(entity.angle) === -1) return new T.Vector3(0, 0, 1);
    if (num(entity.angle) === -2) return new T.Vector3(0, 0, -1);
    return new T.Vector3(Math.cos(pitch) * Math.cos(yaw), Math.cos(pitch) * Math.sin(yaw), Math.sin(pitch));
}
export function ambientLight(cube, normal) {
    const result = [0, 0, 0];
    for (let axis = 0; axis < 3; axis++) {
        const face = axis * 2 + (normal[axis] < 0 ? 1 : 0), weight = normal[axis] ** 2;
        for (let c = 0; c < 3; c++) result[c] += cube[face][c] * weight;
    }
    return result;
}
export function worldLightAt(light, position, normal = null, ignoreNormals = false) {
    const delta = light.type === 3 ? light.normal.map(v => -v) : light.origin.map((v, i) => v - position[i]),
        distance = Math.hypot(...delta), direction = delta.map(v => v / Math.max(0.001, distance));
    if (light.type === 5 || light.radius > 0 && distance >= light.radius) return [0, 0, 0];
    let amount = 1;
    if (light.type !== 3) {
        const [constant, linear, quadratic] = light.attenuation;
        amount = light.type === 0 ? 1 / Math.max(1, distance * distance)
            : light.type === 4 ? Math.max(0, light.radius - distance)
            : 1 / Math.max(0.0001, constant + linear * distance + quadratic * distance * distance);
    }
    if (light.type === 0 || light.type === 2) {
        const cosine = -direction.reduce((sum, v, i) => sum + v * light.normal[i], 0);
        if (cosine <= light.outer) return [0, 0, 0];
        amount *= light.type === 0 ? Math.max(0, cosine) : Math.min(1, Math.max(0, (cosine - light.outer) / Math.max(0.0001, light.inner - light.outer))) ** Math.max(0, light.exponent);
    }
    if (normal && !ignoreNormals) amount *= Math.max(0, normal.reduce((sum, v, i) => sum + v * direction[i], 0));
    return light.color.map(value => value * amount);
}
export class LightingSystem {
    constructor() { this.clear(); }
    clear() {
        for (const light of this.dynamic ?? []) light.object.dispose();
        this.worldLights = []; this.dynamic = []; this.suns = []; this.patterns = new Map();
        this.hasProjectedShadows = this.needsShadowUpdate = false;
        this.propSamples = new WeakMap();
        this.ambientSamples = new WeakMap();
        this.pointMasks = { value: Array.from({ length: 32 }, () => new T.Vector2(1, 1)) };
        this.spotMasks = { value: Array.from({ length: 32 }, () => new T.Vector2(1, 1)) };
    }
    style(style, seconds) {
        return lightStyleValue(style, seconds, this.patterns);
    }
    setup(data, view) {
        this.worldLights = data.lighting?.worldLights ?? [];
        this.visibility = view.visibility; this.entities = data.entities;
        this.patterns = lightStylePatterns(data.entities);
        for (const entity of data.entities) {
            const projected = entity.classname === "env_projectedtexture";
            if (entity.classname !== "light_dynamic" && !projected) continue;
            if (this.dynamic.length >= 32) { log.warn("dynamic light limit reached", { limit: 32 }); break; }
            const flags = projected ? (entity.lightworld === "0" ? 2 : 0) : num(entity.spawnflags),
                radius = Math.max(0, projected ? num(entity.farz, 750) : num(entity.distance, 0)),
                outer = projected ? num(entity.lightfov, 90) / 2 : num(entity._cone),
                rgb = numbers(projected ? entity.lightcolor ?? "255 255 255" : entity._light),
                exponent = projected ? 0 : Math.max(-127, Math.min(127, num(entity.brightness))),
                color = new T.Color().setRGB(...rgb.map(v => Math.max(0, v / 255) * 2 ** exponent)),
                direction = lightDirection(entity, data.entities);
            const object = outer > 0 ? new T.SpotLight(color, Math.PI, radius, Math.min(Math.PI / 2, outer * Math.PI / 180),
                1 - Math.cos(num(entity._inner_cone) * Math.PI / 180) / Math.max(0.0001, Math.cos(outer * Math.PI / 180)), 0)
                : new T.PointLight(color, Math.PI, radius, 0);
            object.position.fromArray(numbers(entity.origin));
            if (projected) {
                object.castShadow = entity.enableshadows !== "0";
                object.shadow.mapSize.set(1024, 1024); object.shadow.camera.near = Math.max(0.1, num(entity.nearz, 4));
                object.shadow.camera.far = radius; object.shadow.bias = -0.0002;
            }
            if (object.isSpotLight) {
                // Three's penumbra is an angle ratio, Source stores cone angles.
                object.penumbra = Math.min(1, Math.max(0, 1 - num(entity._inner_cone) / Math.max(0.001, outer)));
                object.shadow.camera.up.set(0, 0, 1).applyAxisAngle(direction, num(numbers(entity.angles)[2]) * Math.PI / 180);
                object.target.position.copy(object.position).add(direction); view.scene.add(object.target);
            }
            view.scene.add(object);
            this.dynamic.push({ object, entity, flags, projected, base: Math.PI,
                enabled: !projected || !!(num(entity.spawnflags) & 1), style: num(entity.style) });
        }
        this.needsShadowUpdate = this.dynamic.some(light => light.object.castShadow);
        this.hasProjectedShadows = this.needsShadowUpdate;
        this.refreshMasks();
        log.info("lighting initialized", { bakedWorldLights: this.worldLights.length, runtime: this.dynamic.length,
            sunLights: this.worldLights.filter(l => l.type === 3).length, sunSprites: data.entities.filter(e => e.classname === "env_sun").length });
    }
    refreshMasks() {
        // Three orders shadow/cookie lights before other lights in each list.
        const score = light => (light.object.castShadow ? 2 : 0) + (light.object.map ? 1 : 0);
        this.dynamic.sort((a, b) => score(b) - score(a));
        let point = 0, spot = 0;
        for (const light of this.dynamic) {
            const uniform = light.object.isSpotLight ? this.spotMasks : this.pointMasks, index = light.object.isSpotLight ? spot++ : point++;
            uniform.value[index].set(light.flags & 2 ? 0 : 1, light.flags & 1 ? 0 : 1);
        }
    }
    update(seconds) {
        for (const light of this.dynamic) light.object.intensity = light.enabled ? light.base * this.style(light.style, seconds) : 0;
    }
    select(position) {
        const candidates = [];
        for (const light of this.worldLights) {
            if (light.flags & 1 || light.type === 5 || this.style(light.style, 0) === 0) continue;
            const color = worldLightAt(light, position), weight = Math.max(...color);
            if (weight >= 0.001) candidates.push({ light, weight });
        }
        // Only the strongest four unobstructed lights contribute. Test in
        // strength order so weaker candidates need no BSP traces at all.
        candidates.sort((a, b) => b.weight - a.weight);
        const selected = [];
        for (const { light } of candidates) {
            const end = light.type === 3 ? position.map((v, i) => v - light.normal[i] * 65536) : light.origin;
            const trace = this.visibility.trace(position, end);
            if (light.type === 3 ? !trace.sky : trace.fraction < 0.999) continue;
            selected.push(light);
            if (selected.length === 4) break;
        }
        return selected;
    }
    sampleAmbient(position, seconds, ambient) {
        let sample = this.ambientSamples.get(ambient);
        if (!sample || sample.lights !== this.worldLights || sample.visibility !== this.visibility ||
            position.some((v, i) => v !== sample.position[i])) {
            sample = {
                position: [...position], lights: this.worldLights, visibility: this.visibility,
                cube: this.visibility?.ambientCubeAt(position) ?? Array.from({ length: 6 }, () => this.worldLights.length ? [0, 0, 0] : [1, 1, 1]),
                direct: this.visibility?.trace ? this.select(position) : [],
            };
            this.ambientSamples.set(ambient, sample);
        }
        sample.cube.forEach((color, face) => ambient.cube.value[face].setRGB(...color));
        // Cache spatial queries only: animated light styles still advance.
        for (let i = 0; i < 4; i++) {
            const light = sample.direct[i];
            if (!light) { ambient.colors.value[i].setRGB(0, 0, 0); continue; }
            const color = worldLightAt(light, position).map(value => value * this.style(light.style, seconds));
            ambient.colors.value[i].setRGB(...color);
            ambient.directions.value[i].fromArray(light.type === 3 ? light.normal.map(v => -v) : light.origin.map((v, axis) => v - position[axis])).normalize();
        }
    }
    propTexture(data, group, geometry, view, meshIndex) {
        const vertices = data.positions.length / 3, max = view.renderer.capabilities.maxTextureSize,
            width = Math.min(1024, max, Math.max(1, vertices * group.length)), height = Math.ceil(vertices * group.length / width);
        if (height > max || vertices * group.length > 4000000) throw new Error("Prop lighting texture exceeds budget");
        const pixels = new Float32Array(width * height * 4), transform = new T.Object3D(), normalMatrix = new T.Matrix3();
        const p = new T.Vector3(), normal = new T.Vector3(), vertexPosition = [], normalValues = [];
        group.forEach((prop, instance) => {
            transform.position.fromArray(prop.origin); view.angles(transform, prop.angles); transform.scale.setScalar(prop.scale); transform.updateMatrix();
            normalMatrix.getNormalMatrix(transform.matrix);
            const baked = prop.bakedLighting;
            const colors = baked && baked.checksum === data.checksum && baked.meshes[meshIndex]?.length === vertices * 3 ? baked.meshes[meshIndex] : null;
            let cube, direct;
            if (!colors) {
                const position = prop.lightingOrigin ?? (data.illuminationPosition ? new T.Vector3(...data.illuminationPosition) : geometry.boundingSphere.center.clone()).applyMatrix4(transform.matrix).toArray();
                // The lighting origin is shared by a model's material meshes.
                // Baked VHV colors need neither probes nor world-light traces.
                let samples = this.propSamples.get(prop);
                if (!samples) this.propSamples.set(prop, samples = new Map());
                const key = position.join(",");
                if (!samples.has(key)) samples.set(key, {
                    cube: this.visibility.ambientCubeAt(position) ?? Array.from({ length: 6 }, () => this.worldLights.length ? [0, 0, 0] : [1, 1, 1]),
                    direct: this.select(position),
                });
                ({ cube, direct } = samples.get(key));
            }
            for (let vertex = 0; vertex < vertices; vertex++) {
                let color;
                if (colors) color = colors.subarray(vertex * 3, vertex * 3 + 3);
                else {
                    normal.fromArray(data.normals, vertex * 3).applyMatrix3(normalMatrix).normalize();
                    p.fromArray(data.positions, vertex * 3).applyMatrix4(transform.matrix);
                    normal.toArray(normalValues); p.toArray(vertexPosition);
                    color = ambientLight(cube, normalValues);
                    for (const light of direct) {
                        const value = worldLightAt(light, vertexPosition, normalValues, !!(prop.flags & 8));
                        for (let c = 0; c < 3; c++) color[c] += value[c] * this.style(light.style, 0);
                    }
                }
                const index = (instance * vertices + vertex) * 4;
                pixels[index] = color[0]; pixels[index + 1] = color[1]; pixels[index + 2] = color[2]; pixels[index + 3] = 1;
            }
        });
        const texture = view.track(new T.DataTexture(pixels, width, height, T.RGBAFormat, T.FloatType));
        texture.needsUpdate = true;
        log.debug("prop lighting ready", { instances: group.length, vertices, width, height,
            bakedInstances: group.filter(prop => prop.bakedLighting).length });
        return { texture, vertices, size: new T.Vector2(width, height) };
    }
}

// Extend the existing material hooks so terrain blending and texture transforms
// remain intact. Baked lighting is independent of the runtime light list.
export function lightingMaterial(material, system, { prop = false, baked = null, ambient = null, selfIllum = false, mask = null, tint = new T.Color(1, 1, 1) } = {}) {
    const previous = material.onBeforeCompile, key = material.customProgramCacheKey();
    material.sourceShaderHook = previous; material.sourceShaderKey = key;
    material.onBeforeCompile = program => {
        previous.call(material, program);
        Object.assign(program.uniforms, { sourcePointMasks: system.pointMasks, sourceSpotMasks: system.spotMasks,
            sourceProp: { value: +prop }, sourceSelfIllum: { value: +selfIllum }, sourceMask: { value: mask },
            sourceHasMask: { value: +!!mask }, sourceSelfTint: { value: tint } });
        if (ambient) {
            Object.assign(program.uniforms, { sourceAmbientCube: ambient.cube, sourceStaticDirection: ambient.directions, sourceStaticColor: ambient.colors });
            program.fragmentShader = 'uniform vec3 sourceAmbientCube[6], sourceStaticDirection[4], sourceStaticColor[4];\n' + program.fragmentShader;
        }
        if (baked) {
            Object.assign(program.uniforms, { sourceBaked: { value: baked.texture }, sourceBakedSize: { value: baked.size }, sourceVertexCount: { value: baked.vertices } });
            program.vertexShader = 'uniform sampler2D sourceBaked; uniform vec2 sourceBakedSize; uniform float sourceVertexCount; varying vec3 sourceBakedColor;\n' + program.vertexShader;
            program.vertexShader = program.vertexShader.replace('#include <begin_vertex>', `#include <begin_vertex>
                float sourceIndex = float(gl_InstanceID) * sourceVertexCount + float(gl_VertexID);
                sourceBakedColor = texture2D(sourceBaked, (vec2(mod(sourceIndex, sourceBakedSize.x), floor(sourceIndex / sourceBakedSize.x)) + 0.5) / sourceBakedSize).rgb;`);
            program.fragmentShader = 'varying vec3 sourceBakedColor;\n' + program.fragmentShader;
        }
        program.fragmentShader = `uniform vec2 sourcePointMasks[32], sourceSpotMasks[32]; uniform float sourceProp;
            uniform float sourceSelfIllum, sourceHasMask; uniform sampler2D sourceMask; uniform vec3 sourceSelfTint;\n` + program.fragmentShader;
        program.fragmentShader = program.fragmentShader.replace('#include <lights_pars_begin>', T.ShaderChunk.lights_pars_begin.replace(
            /float getDistanceAttenuation\([\s\S]*?\n}/, `float getDistanceAttenuation(const in float d, const in float radius, const in float decay) {
                return radius > 0.0 ? max(0.0, 1.0 - d / radius) : 0.0;
            }`));
        program.fragmentShader = program.fragmentShader.replace('#include <lights_fragment_begin>', T.ShaderChunk.lights_fragment_begin
            .replace('getPointLightInfo( pointLight, geometryPosition, directLight );', 'getPointLightInfo( pointLight, geometryPosition, directLight ); directLight.color *= mix(sourcePointMasks[i].x, sourcePointMasks[i].y, sourceProp);')
            .replace('getSpotLightInfo( spotLight, geometryPosition, directLight );', 'getSpotLightInfo( spotLight, geometryPosition, directLight ); directLight.color *= mix(sourceSpotMasks[i].x, sourceSpotMasks[i].y, sourceProp);'));
        program.fragmentShader = program.fragmentShader.replace('#include <opaque_fragment>', `
            ${baked ? 'outgoingLight += diffuseColor.rgb * sourceBakedColor;' : ''}
            ${ambient ? `vec3 sourceWorldNormal = inverseTransformDirection(normal, viewMatrix);
                vec3 sourceAmbient = sourceWorldNormal.x * sourceWorldNormal.x * (sourceWorldNormal.x < 0.0 ? sourceAmbientCube[1] : sourceAmbientCube[0])
                    + sourceWorldNormal.y * sourceWorldNormal.y * (sourceWorldNormal.y < 0.0 ? sourceAmbientCube[3] : sourceAmbientCube[2])
                    + sourceWorldNormal.z * sourceWorldNormal.z * (sourceWorldNormal.z < 0.0 ? sourceAmbientCube[5] : sourceAmbientCube[4]);
                for (int i = 0; i < 4; i++) sourceAmbient += sourceStaticColor[i] * max(0.0, dot(sourceWorldNormal, sourceStaticDirection[i]));
                outgoingLight += diffuseColor.rgb * sourceAmbient;` : ''}
            ${!prop && !material.lightMap && material.isMeshLambertMaterial ? 'outgoingLight += diffuseColor.rgb;' : ''}
            #ifdef USE_MAP
                if (sourceSelfIllum > 0.5) {
                    vec3 mask = sourceHasMask > 0.5 ? texture2D(sourceMask, vMapUv).rgb : vec3(texture2D(map, vMapUv).a);
                    outgoingLight = mix(outgoingLight, diffuseColor.rgb * sourceSelfTint, mask);
                }
            #endif
            #include <opaque_fragment>`);
    };
    material.customProgramCacheKey = () => `${key}:SourceLighting:${prop}:${!!baked}:${!!ambient}:${selfIllum}:${!!mask}`;
    material.needsUpdate = true;
    return material;
}
